import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Cell, PieChart, Pie 
} from 'recharts';

import { 
  Plus, Users, MapPin, Shield, Smartphone, Wallet, Trophy, Zap, 
  Activity, AlertTriangle, X, Landmark, Crown, Award, Medal, 
  RefreshCw, Plane, CheckCircle2, Globe, TrendingUp, Search,
  Crosshair, Lock, ShieldAlert, MessageSquare, Power, Battery, TrendingDown,
  Menu, ChevronRight, ArrowUpRight, Clock, Send, Radio, User, CreditCard, Calendar, Camera
} from 'lucide-react';

/**
 * 🏛️ SAHELGUARD OS - V4.5 (INTEGRAL MASTER EDITION)
 * ---------------------------------------------------
 * Architecture : Moussa Yarbanga | Yarmotek Sovereign OS
 * Modules : Broadcast, KYC, Claims (Art. 1-7), Treasury, Vigie.
 * Intégrité : Scellée & Validée - Mars 2026.
 */

// --- CONFIGURATION CARTOGRAPHIQUE ---
if (typeof window !== 'undefined') {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  });
}

const MapRecenter = ({ position }) => {
  const map = useMap();
  useEffect(() => { if (position) map.flyTo(position, 16, { duration: 1.5 }); }, [position, map]);
  return null;
};

// --- COMPOSANT DE CHARGEMENT SOUVERAIN ---
const SovereignLoader = ({ onComplete }) => {
  const [percent, setPercent] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setPercent((p) => {
        if (p >= 100) { clearInterval(interval); setTimeout(onComplete, 800); return 100; }
        return p + 2;
      });
    }, 20);
    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <motion.div exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] bg-[#050505] flex flex-col items-center justify-center">
      <Shield size={80} className="text-emerald-500 mb-8 animate-pulse" strokeWidth={1} />
      <div className="w-64 h-[2px] bg-white/5 rounded-full overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${percent}%` }} className="h-full bg-emerald-500 shadow-[0_0_20px_#10b981]" />
      </div>
      <p className="text-[10px] font-black tracking-[0.5em] text-white/30 mt-6 uppercase italic">Yarmotek Sovereign System</p>
    </motion.div>
  );
};

// --- APPLICATION PRINCIPALE ---
const SahelGuardOS = ({ apiUrl }) => {
  const [isBooting, setIsBooting] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('COMMAND'); 
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Data States
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [vaultStats, setVaultStats] = useState({ Total_Encaisse: 0, Coffre_Souverain: 0, Fonds_Expansion_Reseau: 0 });
  const [broadcastMsg, setBroadcastMsg] = useState("");
  
  // KYC State
  const [kycData, setKycData] = useState(null);

  // Sync Data
  const fetchEmpireData = async () => {
    try {
      const [resL, resV, resF] = await Promise.all([
        fetch(`${apiUrl}/api/admin/haut-conseil`),
        fetch(`${apiUrl}/api/admin/treasury`),
        fetch(`${apiUrl}/api/admin/get-all-clients`)
      ]);
      if (resL.ok) setAgents(await resL.json());
      if (resV.ok) setVaultStats(await resV.json());
      if (resF.ok) setClients(await resF.json());
    } catch (e) { console.error("Sync Offline"); }
  };

  useEffect(() => { if (isAuthenticated) fetchEmpireData(); }, [isAuthenticated]);

  if (isBooting) return <SovereignLoader onComplete={() => setIsBooting(false)} />;

  if (!isAuthenticated) {
    return <KYCScreen onVerified={(data) => { setKycData(data); setIsAuthenticated(true); }} />;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-emerald-500/30 pb-48">
      
      {/* NAVIGATION HEADER */}
      <header className="sticky top-0 z-[5000] bg-[#050505]/90 backdrop-blur-2xl border-b border-white/5 p-6 md:p-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Shield size={32} className="text-emerald-500" />
            <h1 className="text-2xl md:text-4xl font-black italic uppercase tracking-tighter">SahelGuard <span className="text-emerald-500">OS</span></h1>
          </div>
          <nav className="hidden md:flex bg-white/5 p-1.5 rounded-full border border-white/10">
            <TabBtn id="COMMAND" label="Conseil" active={activeTab} set={setActiveTab} icon={<Trophy size={14}/>} />
            <TabBtn id="VIGIE" label="Vigie Map" active={activeTab} set={setActiveTab} icon={<MapPin size={14}/>} />
            <TabBtn id="TREASURY" label="Trésorerie" active={activeTab} set={setActiveTab} icon={<Landmark size={14}/>} />
            <TabBtn id="CLAIM" label="Sinistre" active={activeTab} set={setActiveTab} icon={<AlertTriangle size={14}/>} />
          </nav>
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-3 bg-white/5 rounded-2xl"><Menu size={24}/></button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-12">
        <AnimatePresence mode="wait">
          
          {/* MODULE 1: COMMAND & BROADCAST */}
          {activeTab === 'COMMAND' && (
            <motion.div key="command" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-12">
              <div className="bg-gradient-to-r from-emerald-500/10 to-transparent border border-emerald-500/20 p-8 rounded-[3.5rem] relative overflow-hidden">
                <Radio className="absolute right-10 top-1/2 -translate-y-1/2 opacity-5 text-emerald-500" size={150} />
                <h3 className="text-xl font-black italic uppercase mb-2">Émission Souveraine</h3>
                <div className="relative max-w-xl mt-6">
                  <input 
                    type="text" value={broadcastMsg} onChange={(e) => setBroadcastMsg(e.target.value)}
                    placeholder="Ordre de mission aux agents..." 
                    className="w-full bg-black/60 border border-white/10 rounded-full py-5 px-8 text-sm outline-none focus:border-emerald-500 font-bold"
                  />
                  <button className="absolute right-2 top-1/2 -translate-y-1/2 bg-emerald-500 text-black p-3.5 rounded-full hover:scale-105 transition-transform">
                    <Send size={18} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {agents.map((agent, i) => <AgentCard key={i} agent={agent} />)}
              </div>
            </motion.div>
          )}

          {/* MODULE 2: VIGIE MAP */}
          {activeTab === 'VIGIE' && (
            <motion.div key="vigie" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-[700px] rounded-[3.5rem] overflow-hidden border border-white/5 shadow-3xl">
              <MapContainer center={[12.3713, -1.5197]} zoom={12} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                {clients.map(pos => (
                  <Marker key={pos.token} position={[pos.lat, pos.lng]}>
                    <Popup>
                      <div className="p-4 bg-black text-white min-w-[150px]">
                        <p className="font-black italic uppercase">{pos.userFirstName}</p>
                        <p className="text-[10px] text-emerald-500 font-mono mt-2">BATT: {pos.battery}%</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </motion.div>
          )}

          {/* MODULE 3: TREASURY & LEDGER */}
          {activeTab === 'TREASURY' && (
            <motion.div key="treasury" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-10">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-gradient-to-br from-[#10b981] to-[#064e3b] p-12 rounded-[3.5rem] shadow-2xl relative overflow-hidden">
                  <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/50">Coffre Souverain (95%)</p>
                  <h3 className="text-6xl md:text-8xl font-black italic mt-4">{(vaultStats.Coffre_Souverain || 0).toLocaleString()} <span className="text-xl opacity-30">CFA</span></h3>
                  <div className="mt-10 flex gap-4">
                     <div className="bg-black/20 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/10 font-black italic">EXPANSION : {(vaultStats.Fonds_Expansion_Reseau || 0).toLocaleString()} F</div>
                  </div>
                </div>
                <div className="bg-[#0A0A0A] border border-white/5 rounded-[3.5rem] p-10 flex flex-col items-center justify-center">
                   <PieChart width={200} height={200}>
                     <Pie data={[{v:70},{v:30}]} innerRadius={60} outerRadius={80} dataKey="v" stroke="none">
                       <Cell fill="#10b981"/><Cell fill="#ffffff05"/>
                     </Pie>
                   </PieChart>
                   <p className="text-[10px] font-black uppercase text-white/30 tracking-widest mt-4 italic">Performance UEMOA</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* MODULE 4: CLAIM (SINISTRE ARTICLE 1-7) */}
          {activeTab === 'CLAIM' && (
            <motion.div key="claim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl mx-auto space-y-8">
              <ClaimForm />
            </motion.div>
          )}

        </AnimatePresence>
      </main>

      {/* FOOTER STATUS */}
      <footer className="fixed bottom-0 left-0 right-0 p-8 pointer-events-none">
        <div className="max-w-4xl mx-auto bg-black/90 backdrop-blur-3xl border border-white/10 rounded-[2.5rem] p-5 flex items-center justify-between pointer-events-auto shadow-4xl">
           <div className="flex items-center gap-4 ml-6">
              <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-ping" />
              <span className="text-[11px] font-black uppercase tracking-widest italic">SahelGuard OS <span className="text-emerald-500">v4.5 PRO</span></span>
           </div>
           <div className="flex gap-10 mr-6 items-center">
              <p className="text-[10px] font-mono text-emerald-500/50">KYC_VERIFIED: {kycData?.city || 'SYSTEM'}</p>
              <div className="h-6 w-[1px] bg-white/10" />
              <p className="text-[10px] font-mono text-white/40">{new Date().toLocaleTimeString()}</p>
           </div>
        </div>
      </footer>
    </div>
  );
};

// --- COMPOSANTS DE STRUCTURE ---

const AgentCard = ({ agent }) => (
  <div className="bg-[#0A0A0A] border border-white/5 p-10 rounded-[3.5rem] hover:border-emerald-500/40 transition-all group">
    <div className="p-4 bg-white/5 rounded-[1.5rem] w-fit mb-8 group-hover:bg-emerald-500 group-hover:text-black transition-colors"><Shield size={24}/></div>
    <h4 className="text-2xl font-black italic uppercase leading-none tracking-tight">{agent.biz_name}</h4>
    <p className="text-[10px] font-black text-white/20 uppercase mt-2 tracking-widest">{agent.city} • {agent.rank}</p>
    <div className="mt-10 pt-8 border-t border-white/5 flex justify-between font-black italic">
      <div><p className="text-[8px] text-white/30 uppercase tracking-widest">Actifs</p><p className="text-2xl text-emerald-500">{agent.total_enrolled || 0}</p></div>
      <div className="text-right"><p className="text-[8px] text-white/30 uppercase tracking-widest">Score</p><p className="text-2xl text-blue-400">{agent.points_voyage || 0}</p></div>
    </div>
  </div>
);

const KYCScreen = ({ onVerified }) => {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({ city: '', idNum: '', email: '' });
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6 text-white">
      <div className="max-w-md w-full bg-[#0A0A0A] p-12 rounded-[4rem] border border-white/5 text-center">
        <ShieldCheck size={60} className="text-emerald-500 mx-auto mb-8" />
        <h2 className="text-3xl font-black italic uppercase italic tracking-tighter mb-10">Authentification <span className="text-emerald-500">Agent</span></h2>
        <div className="space-y-6 mb-10 text-left">
           <div className="space-y-2">
              <label className="text-[9px] font-black uppercase text-white/20 ml-4">Ville de Résidence</label>
              <input type="text" onChange={(e)=>setData({...data, city: e.target.value})} className="w-full bg-black/50 border border-white/10 rounded-3xl py-4 px-6 text-sm focus:border-emerald-500 outline-none" placeholder="Ex: Ouagadougou" />
           </div>
           <div className="space-y-2">
              <label className="text-[9px] font-black uppercase text-white/20 ml-4">N° CNI ou Passeport</label>
              <input type="text" onChange={(e)=>setData({...data, idNum: e.target.value})} className="w-full bg-black/50 border border-white/10 rounded-3xl py-4 px-6 text-sm focus:border-emerald-500 outline-none" placeholder="BXXXXXXXX" />
           </div>
        </div>
        <button onClick={() => onVerified(data)} className="w-full bg-emerald-500 text-black py-5 rounded-[2rem] font-black uppercase italic hover:scale-105 transition-transform">Accéder au Système</button>
      </div>
    </div>
  );
};

const ClaimForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [checks, setChecks] = useState({ pv: false, valid: false, proof: false });
  const allChecked = checks.pv && checks.valid && checks.proof;

  if (submitted) return (
    <div className="text-center p-20 bg-emerald-500/5 rounded-[4rem] border border-emerald-500/20">
      <CheckCircle2 size={80} className="text-emerald-500 mx-auto mb-6" />
      <h3 className="text-3xl font-black italic uppercase">Sinistre Scellé</h3>
      <p className="text-[10px] text-white/30 uppercase mt-4 tracking-widest italic leading-loose">
        Le rapport a été transmis au bureau central.<br/>Un SMS a été envoyé au Super Admin.<br/>Rappel : Franchise de 10% exigible en Cash.
      </p>
    </div>
  );

  return (
    <div className="bg-[#0A0A0A] border border-white/5 p-12 rounded-[4rem]">
      <div className="flex items-center gap-4 mb-10">
        <div className="p-4 bg-red-500/10 rounded-3xl text-red-500"><ShieldAlert size={32}/></div>
        <h3 className="text-2xl font-black italic uppercase">Déclaration de Vol <span className="text-red-500">Pact Art. 5</span></h3>
      </div>
      <div className="space-y-4 mb-10">
        <CheckItem label="PV de Police original avec IMEI" checked={checks.pv} onCheck={()=>setChecks({...checks, pv: !checks.pv})} />
        <CheckItem label="Licence SahelGuard Active au Sinistre" checked={checks.valid} onCheck={()=>setChecks({...checks, valid: !checks.valid})} />
        <CheckItem label="Preuve Numérique Enregistrée (GPS/Photo)" checked={checks.proof} onCheck={()=>setChecks({...checks, proof: !checks.proof})} />
      </div>
      <div className="bg-amber-500/5 border border-amber-500/20 p-6 rounded-3xl mb-10">
        <p className="text-[10px] font-black text-amber-500 uppercase italic">⚠️ ART. 3 : Aucun remboursement cash - Remplacement matériel uniquement.</p>
      </div>
      <button 
        disabled={!allChecked}
        onClick={() => setSubmitted(true)}
        className={`w-full py-6 rounded-[2.5rem] font-black uppercase italic transition-all ${allChecked ? 'bg-red-500 text-white hover:scale-105' : 'bg-white/5 text-white/10 cursor-not-allowed'}`}
      >
        Transmettre le dossier
      </button>
    </div>
  );
};

const CheckItem = ({ label, checked, onCheck }) => (
  <div onClick={onCheck} className={`p-5 rounded-[2rem] border cursor-pointer transition-all flex items-center justify-between ${checked ? 'bg-emerald-500/10 border-emerald-500' : 'bg-black border-white/5'}`}>
    <span className={`text-[11px] font-black uppercase ${checked ? 'text-emerald-500' : 'text-white/20'}`}>{label}</span>
    {checked ? <CheckCircle2 size={20} className="text-emerald-500" /> : <div className="w-5 h-5 rounded-full border-2 border-white/5" />}
  </div>
);

const TabBtn = ({ id, label, active, set, icon }) => (
  <button onClick={() => set(id)} className={`px-6 py-3.5 rounded-full flex items-center gap-3 text-[10px] font-black uppercase tracking-widest transition-all ${active === id ? 'bg-emerald-500 text-black shadow-lg shadow-emerald-500/40' : 'text-white/40 hover:text-white hover:bg-white/5'}`}>
    {icon} {label}
  </button>
);

const ShieldCheck = ({ size, className }) => <Shield size={size} className={className} />;

export default SahelGuardOS;